<template>
  <section v-if="bindingItems.length" class="cs-default-focus" aria-labelledby="default-spatial-focus-title">
    <div class="cs-default-focus__head">
      <div>
        <div id="default-spatial-focus-title" class="cs-default-focus__title">Default 3D Focus</div>
        <div class="cs-default-focus__description">
          Choose the exact relationship paths highlighted by dashboards. Tag Designation remains independent.
        </div>
      </div>
    </div>

    <div class="cs-default-focus__controls">
      <v-select
        :value="selectedTargetKeys"
        :items="bindingItems"
        :disabled="readonly"
        label="Highlight bindings"
        multiple
        chips
        small-chips
        outlined
        dense
        hide-details="auto"
        @change="setTargetBindings" />

      <div
        v-for="selector in filteredTargetSelectors"
        :key="selector.bindingKey"
        class="cs-default-focus__subtypes">
        <v-select
          :value="selector.tagTypes || []"
          :items="tagTypeItems"
          :disabled="readonly"
          :label="`${bindingLabel(selector.bindingKey)} target Tag types (optional)`"
          multiple
          chips
          small-chips
          outlined
          dense
          hide-details="auto"
          @change="setTargetTagTypes(selector.bindingKey, $event)" />
      </div>

      <v-select
        :value="selectedContextKeys"
        :items="contextItems"
        :disabled="readonly"
        label="Context bindings"
        multiple
        chips
        small-chips
        outlined
        dense
        hide-details="auto"
        @change="setContextBindings" />

      <div class="cs-default-focus__fallback">
        <v-switch
          :input-value="includeOwningContext"
          :disabled="readonly"
          color="primary"
          dense
          inset
          hide-details
          class="mt-0"
          @change="setIncludeOwningContext">
          <template #label>
            <span>Include owning context</span>
          </template>
        </v-switch>
        <div class="cs-default-focus__description">
          Anomaly, CML, and Inspection POIs keep their owning component or tag gray.
        </div>
      </div>

      <div class="cs-default-focus__fallback">
        <v-switch
          :input-value="useMasterTagAsPoiFallback"
          :disabled="readonly"
          color="primary"
          dense
          inset
          hide-details
          class="mt-0"
          @change="setNoTargetFallback">
          <template #label>
            <span>Use Master Tag as fallback POI</span>
          </template>
        </v-switch>
        <div class="cs-default-focus__description">
          Off keeps targetless visible anchors as gray context instead of coloring them as targets.
        </div>
      </div>
    </div>
  </section>
</template>

<script>
  import {
    SpatialNoTargetPolicy,
    SpatialTagTypeOptions,
    normalizeSpatialIncludeOwningContext,
    spatialNoTargetPolicyForFallback,
  } from '../../model/definitionVocabulary';

  const emptyFocus = () => ({
    targets: [],
    contexts: [],
    noTarget: SpatialNoTargetPolicy,
  });

  export default {
    name: 'DefaultSpatialFocusEditor',
    props: {
      model: { type: Object, required: true },
      graph: { type: Object, default: null },
      readonly: { type: Boolean, default: false },
    },
    computed: {
      focus() {
        return this.model.state.spatialFocus || emptyFocus();
      },
      bindingItems() {
        return this.model.spatialBindings().map((binding) => ({
          text: binding.label,
          value: binding.bindingKey,
        }));
      },
      bindingsByKey() {
        return new Map(this.model.spatialBindings().map((binding) => [binding.bindingKey, binding]));
      },
      selectedTargetKeys() {
        return this.focus.targets.map((selector) => selector.bindingKey);
      },
      selectedContextKeys() {
        return this.focus.contexts.map((selector) => selector.bindingKey);
      },
      filteredTargetSelectors() {
        return this.focus.targets.filter((selector) => (
          this.bindingsByKey.get(selector.bindingKey)?.canFilterTagTypes
        ));
      },
      contextItems() {
        return this.model.spatialBindings().map((binding) => {
          const target = this.focus.targets.find((selector) => selector.bindingKey === binding.bindingKey);
          const needsSubtypeSplit = !!target && !(target.tagTypes && target.tagTypes.length);
          return {
            text: needsSubtypeSplit
              ? `${binding.label} — choose target Tag types first`
              : binding.label,
            value: binding.bindingKey,
            disabled: needsSubtypeSplit,
          };
        });
      },
      tagTypeItems() {
        return SpatialTagTypeOptions;
      },
      useMasterTagAsPoiFallback() {
        return this.focus.noTarget !== spatialNoTargetPolicyForFallback(false);
      },
      includeOwningContext() {
        return normalizeSpatialIncludeOwningContext(this.focus.includeOwningContext);
      },
    },
    methods: {
      bindingLabel(bindingKey) {
        return this.bindingsByKey.get(bindingKey)?.label || bindingKey;
      },
      commitFocus(targets, contexts, policy = {}) {
        this.model.ensureV3TraversalKeys(this.graph);
        this.model.setSpatialFocus({
          targets,
          contexts,
          noTarget: policy.noTarget ?? this.focus.noTarget,
          includeOwningContext: policy.includeOwningContext
            ?? this.focus.includeOwningContext,
        });
      },
      normalizedContexts(targets, bindingKeys) {
        return bindingKeys.flatMap((bindingKey) => {
          const target = targets.find((selector) => selector.bindingKey === bindingKey);
          if (target && !(target.tagTypes && target.tagTypes.length)) return [];
          return [{ bindingKey, selection: target ? 'remaining' : 'all' }];
        });
      },
      setTargetBindings(bindingKeys) {
        const targets = bindingKeys.map((bindingKey) => (
          this.focus.targets.find((selector) => selector.bindingKey === bindingKey)
          || { bindingKey }
        ));
        const contexts = this.normalizedContexts(targets, this.selectedContextKeys);
        this.commitFocus(targets, contexts);
      },
      setTargetTagTypes(bindingKey, tagTypes) {
        const targets = this.focus.targets.map((selector) => {
          if (selector.bindingKey !== bindingKey) return selector;
          return tagTypes.length ? { bindingKey, tagTypes: tagTypes.slice() } : { bindingKey };
        });
        const contexts = this.normalizedContexts(targets, this.selectedContextKeys);
        this.commitFocus(targets, contexts);
      },
      setContextBindings(bindingKeys) {
        this.commitFocus(
          this.focus.targets,
          this.normalizedContexts(this.focus.targets, bindingKeys),
        );
      },
      setNoTargetFallback(enabled) {
        this.commitFocus(
          this.focus.targets,
          this.focus.contexts,
          { noTarget: spatialNoTargetPolicyForFallback(enabled) },
        );
      },
      setIncludeOwningContext(enabled) {
        this.commitFocus(
          this.focus.targets,
          this.focus.contexts,
          { includeOwningContext: enabled !== false },
        );
      },
    },
  };
</script>

<style scoped>
  .cs-default-focus {
    margin-top: var(--ds-space-md);
    padding: var(--ds-space-md);
    border: 1px solid var(--v-border-base);
    border-radius: var(--ds-radius-md);
    background: var(--v-bg2-base);
  }

  .cs-default-focus__head {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: var(--ds-space-md);
    margin-bottom: var(--ds-space-md);
  }

  .cs-default-focus__title {
    font-weight: 600;
    color: var(--v-text-base);
  }

  .cs-default-focus__description {
    margin-top: var(--ds-space-xs);
    color: var(--v-text_secondary-base);
  }

  .cs-default-focus__controls {
    display: grid;
    gap: var(--ds-space-sm);
  }

  .cs-default-focus__fallback {
    padding-top: var(--ds-space-xs);
  }

  @media (max-width: 700px) {
    .cs-default-focus__head {
      flex-direction: column;
    }
  }
</style>
